import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Badge,
  Input,
  Spinner,
  Avatar,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@fluentui/react-components';
import {
  AddRegular,
  DeleteRegular,
  EditRegular,
  QrCodeRegular,
  SearchRegular,
  LocationRegular,
  DismissRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../../components/layout';
import { PermissionGate, ProtectedButton } from '../../../components/common';
import { TablePagination, SearchableDropdown } from '../../../components/common';
import {
  ApiClient,
  getErrorMessage,
  newToast,
  API_URL,
} from '../../../configurations/index.config';
import type { Checkpoint, Site, Zone, Client } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  filters: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#faf9f8',
    borderRadius: '8px',
  },
  filterRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  filterDropdown: {
    flex: '1 1 25%',
    minWidth: '0',
  },
  searchInput: {
    width: '40%',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  tableHeader: {
    backgroundColor: '#faf9f8',
    borderBottom: '1px solid #edebe9',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #edebe9',
    ':hover': {
      backgroundColor: '#f3f2f1',
      cursor: 'pointer',
    },
  },
  td: {
    padding: '12px 16px',
    color: '#323130',
  },
  checkpointCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px',
  },
  emptyStateIcon: {
    fontSize: '48px',
    color: '#c8c6c4',
    marginBottom: '16px',
  },
  qrPreview: {
    maxWidth: '100px',
    maxHeight: '100px',
    border: '1px solid #edebe9',
    borderRadius: '4px',
  },
});

const CheckpointsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [clients, setClients] = useState<Client[]>([]);

  const [selectedZoneId, setSelectedZoneId] = useState<string>('all');
  const [selectedClientId, setSelectedClientId] = useState<string>('all');
  const [selectedSiteId, setSelectedSiteId] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState<string>('');
  const [qrCheckpointName, setQrCheckpointName] = useState<string>('');
  const [qrName, setQrName] = useState<string>('');

  useEffect(() => {
    fetchData();
  }, [selectedSiteId, selectedStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedSiteId !== 'all') {
        params.site_id = Number(selectedSiteId);
      }
      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }

      const [checkpointsRes, sitesRes, zonesRes, clientsRes] = await Promise.all([
        ApiClient.get('/get-checkpoints', { params }),
        ApiClient.get('/get-sites'),
        ApiClient.get('/get-zones'),
        ApiClient.get('/get-clients'),
      ]);

      if (checkpointsRes.data.status === 'success') {
        setCheckpoints(checkpointsRes.data.data || []);
      }

      if (sitesRes.data.status === 'success') {
        setSites(sitesRes.data.data || []);
      }

      if (zonesRes.data.status === 'success') {
        setZones(zonesRes.data.data || []);
      }

      if (clientsRes.data.status === 'success') {
        setClients(clientsRes.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const filteredCheckpoints = useMemo(() => {
    return checkpoints.filter(checkpoint => {
      const matchesSearch = searchQuery
        ? checkpoint.checkpoint_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          checkpoint.site_name?.toLowerCase().includes(searchQuery.toLowerCase())
        : true;
      return matchesSearch;
    });
  }, [checkpoints, searchQuery]);

  const totalItems = filteredCheckpoints.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredCheckpoints.slice(startIndex, endIndex);

  const handleViewDetails = (id: number) => {
    navigate(`/account/checkpoint-details/${id}`);
  };

  const handleEdit = (checkpoint: Checkpoint) => {
    navigate(`/account/checkpoint-details/${checkpoint.id}`);
  };

  const handleDelete = async (checkpoint: Checkpoint) => {
    if (!window.confirm(`Delete checkpoint "${checkpoint.checkpoint_name}"?`)) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-checkpoint', {
        id: checkpoint.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Checkpoint deleted successfully');
        fetchData();
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleGenerateQR = async (checkpoint: Checkpoint) => {
    try {
      const response = await ApiClient.post('/generate-qr-batch', {
        checkpoint_ids: [checkpoint.id],
      });

      if (response.data.status === 'success') {
        newToast('success', 'QR code generated successfully');
        const qrImage = response.data.data.qr_images[0];
        const imageUrl = `${API_URL}${qrImage.qr_image_url}`;
        setQrImageUrl(imageUrl);
        setQrCheckpointName(checkpoint.checkpoint_name);
        setQrName(qrImage.qr_name);
        setQrModalOpen(true);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleDownloadQR = () => {
    if (qrImageUrl) {
      const link = document.createElement('a');
      link.href = qrImageUrl;
      link.download = `QR-${qrCheckpointName}.png`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleAddCheckpoint = () => {
    navigate('/account/add-checkpoint');
  };

  const handleQRManagement = () => {
    navigate('/account/qr-batch-generation');
  };

  return (
    <PermissionGate privilege="checkpoints.view" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <Text className={styles.pageTitle}>Manage Checkpoints</Text>
          <Text className={styles.pageSubtitle}>
            Create and manage checkpoint locations for guard patrols with QR code verification
          </Text>
        </div>

        <ContentCard>
          <TableCommandBar
            title={`Checkpoints (${totalItems})`}
            actions={
              <>
                <ProtectedButton
                  privilege="qr.generate"
                  appearance="secondary"
                  icon={<QrCodeRegular />}
                  onClick={handleQRManagement}
                >
                  QR Management
                </ProtectedButton>
                <ProtectedButton
                  privilege="checkpoints.create"
                  appearance="primary"
                  icon={<AddRegular />}
                  onClick={handleAddCheckpoint}
                >
                  Add Checkpoint
                </ProtectedButton>
              </>
            }
          />

          <div className={styles.filters}>
            <div className={styles.filterRow}>
              <div className={styles.filterDropdown}>
                <SearchableDropdown
                  placeholder="Select Zone"
                  selectedValue={selectedZoneId}
                  onSelect={value => {
                    setSelectedZoneId(value);
                    setSelectedClientId('all');
                    setSelectedSiteId('all');
                    setCurrentPage(1);
                  }}
                  options={[
                    { id: 'all', label: 'All Zones' },
                    ...zones.map(zone => ({
                      id: zone.id.toString(),
                      label: zone.name,
                    })),
                  ]}
                />
              </div>

              <div className={styles.filterDropdown}>
                <SearchableDropdown
                  placeholder="Select Client"
                  selectedValue={selectedClientId}
                  onSelect={value => {
                    setSelectedClientId(value);
                    setSelectedSiteId('all');
                    setCurrentPage(1);
                  }}
                  options={[
                    { id: 'all', label: 'All Clients' },
                    ...clients
                      .filter(client => {
                        if (selectedZoneId === 'all') {
                          return true;
                        }
                        return sites.some(
                          site =>
                            site.client_id === client.id && site.zone_id === Number(selectedZoneId)
                        );
                      })
                      .map(client => ({
                        id: client.id.toString(),
                        label: client.client_name,
                      })),
                  ]}
                />
              </div>

              <div className={styles.filterDropdown}>
                <SearchableDropdown
                  placeholder="Select Site"
                  selectedValue={selectedSiteId}
                  onSelect={value => {
                    setSelectedSiteId(value);
                    setCurrentPage(1);
                  }}
                  options={[
                    { id: 'all', label: 'All Sites' },
                    ...sites
                      .filter(site => {
                        if (
                          selectedClientId !== 'all' &&
                          site.client_id !== Number(selectedClientId)
                        ) {
                          return false;
                        }
                        if (selectedZoneId !== 'all' && site.zone_id !== Number(selectedZoneId)) {
                          return false;
                        }
                        return true;
                      })
                      .map(site => ({
                        id: site.id.toString(),
                        label: site.name,
                      })),
                  ]}
                />
              </div>

              <div className={styles.filterDropdown}>
                <SearchableDropdown
                  placeholder="Filter by Status"
                  selectedValue={selectedStatus}
                  onSelect={value => {
                    setSelectedStatus(value);
                    setCurrentPage(1);
                  }}
                  options={[
                    { id: 'all', label: 'All Status' },
                    { id: 'active', label: 'Active' },
                    { id: 'inactive', label: 'Inactive' },
                  ]}
                />
              </div>
            </div>

            <div className={styles.searchInput}>
              <Input
                contentBefore={<SearchRegular />}
                placeholder="Search checkpoints..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
              <Spinner label="Loading checkpoints..." />
            </div>
          ) : paginatedData.length === 0 ? (
            <div className={styles.emptyState}>
              <LocationRegular className={styles.emptyStateIcon} />
              <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                No checkpoints found
              </Text>
              <Text style={{ color: '#605e5c' }}>
                {searchQuery
                  ? 'Try adjusting your search or filters'
                  : 'Create your first checkpoint to get started'}
              </Text>
            </div>
          ) : (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th className={styles.th}>Checkpoint</th>
                  <th className={styles.th}>Site</th>
                  <th className={styles.th}>Location</th>
                  <th className={styles.th}>Validation Radius</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map(checkpoint => (
                  <tr
                    key={checkpoint.id}
                    className={styles.tr}
                    onClick={() => handleViewDetails(checkpoint.id)}
                  >
                    <td className={styles.td}>
                      <div className={styles.checkpointCell}>
                        <Avatar icon={<LocationRegular />} color="brand" size={32} />
                        <div>
                          <Text weight="semibold" style={{ display: 'block' }}>
                            {checkpoint.checkpoint_name}
                          </Text>
                          {checkpoint.description && (
                            <Text size={200} style={{ color: '#605e5c' }}>
                              {checkpoint.description}
                            </Text>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className={styles.td}>
                      <Text>{checkpoint.site_name || '—'}</Text>
                    </td>
                    <td className={styles.td}>
                      {checkpoint.expected_gps_lat && checkpoint.expected_gps_lng ? (
                        <Text size={200}>
                          {checkpoint.expected_gps_lat}, {checkpoint.expected_gps_lng}
                        </Text>
                      ) : (
                        <Text style={{ color: '#a19f9d' }}>No GPS</Text>
                      )}
                    </td>
                    <td className={styles.td}>
                      <Text>{checkpoint.validation_radius_m}m</Text>
                    </td>
                    <td className={styles.td}>
                      <Badge
                        appearance="tint"
                        color={checkpoint.status === 'active' ? 'success' : 'warning'}
                      >
                        {checkpoint.status}
                      </Badge>
                    </td>
                    <td className={styles.td} onClick={e => e.stopPropagation()}>
                      <div className={styles.actions}>
                        <ProtectedButton
                          privilege="qr.generate"
                          appearance="subtle"
                          size="small"
                          icon={<QrCodeRegular />}
                          onClick={() => handleGenerateQR(checkpoint)}
                        />
                        <ProtectedButton
                          privilege="checkpoints.update"
                          appearance="subtle"
                          size="small"
                          icon={<EditRegular />}
                          onClick={() => handleEdit(checkpoint)}
                        />
                        <ProtectedButton
                          privilege="checkpoints.delete"
                          appearance="subtle"
                          size="small"
                          icon={<DeleteRegular />}
                          onClick={() => handleDelete(checkpoint)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && paginatedData.length > 0 && (
            <TablePagination
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={value => {
                setItemsPerPage(value);
                setCurrentPage(1);
              }}
            />
          )}
        </ContentCard>
      </div>

      <Dialog
        open={qrModalOpen}
        onOpenChange={(_, data) => setQrModalOpen(data.open)}
        modalType="modal"
      >
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  onClick={() => setQrModalOpen(false)}
                />
              }
            >
              QR Code - {qrCheckpointName}
            </DialogTitle>
            <DialogContent>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '24px',
                  gap: '16px',
                }}
              >
                {qrName && (
                  <Text weight="semibold" size={500}>
                    {qrName}
                  </Text>
                )}
                {qrImageUrl && (
                  <img
                    src={qrImageUrl}
                    alt={`QR Code for ${qrCheckpointName}`}
                    style={{ maxWidth: '300px', maxHeight: '300px', border: '1px solid #edebe9' }}
                  />
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setQrModalOpen(false)}>
                Close
              </Button>
              <Button
                appearance="primary"
                icon={<ArrowDownloadRegular />}
                onClick={handleDownloadQR}
              >
                Download
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </PermissionGate>
  );
};

export default CheckpointsFluentUI;
